/**
 * The two offer status axes, and the ONLY place either value list is written down (KB-4101).
 *
 * `signupStatus` is the registration WINDOW; `availabilityStatus` is CAPACITY. They are
 * orthogonal (KB-2876; firestore-data-model.md, "TWO ORTHOGONAL AXES"): regEvents drive the window,
 * capacity signals drive availability, and nothing derives one from the other. `su_waitlist` and
 * `su_full` do not exist — waitlist and full are capacity (`av_waitlist`, `av_fullNoWaitlist`).
 *
 * Every TS type, zod schema and write path imports these lists. The one sanctioned mirror is the
 * `signUpStatus`/`availStatus` seed rows in `src/services/sortableOptionService.ts`, which must stay
 * literal because `scripts/camp-data/_reconcile/canonicalStatusEnums.cjs` parses them for the pipeline.
 * `src/services/offerStatusEnums.drift.test.ts` fails if that mirror or the pipeline's parsed lists
 * drift from these, or if a full list is hand-copied into any other non-test TS file under `src/` or
 * `functions/src/` (it does not scan the `.cjs` pipeline under `scripts/`).
 *
 * Off-enum input is coerced PER AXIS to that axis's own `*_unknown`, never to a value on the other
 * axis. Absent input is the no-evidence default and is not logged; a present off-enum value is.
 */

export const SIGNUP_STATUSES = ["su_notYet", "su_openLimited", "su_open", "su_closed", "su_unknown"] as const;
export const AVAILABILITY_STATUSES = ["av_space", "av_limited", "av_waitlist", "av_fullNoWaitlist", "av_unknown"] as const;

export type SignupStatus = (typeof SIGNUP_STATUSES)[number];
export type AvailabilityStatus = (typeof AVAILABILITY_STATUSES)[number];

export const SIGNUP_STATUS_UNKNOWN: SignupStatus = "su_unknown";
export const AVAILABILITY_STATUS_UNKNOWN: AvailabilityStatus = "av_unknown";

/** Admin dropdown labels, in `SIGNUP_STATUSES` order (`su_openLimited` is a limited AUDIENCE). */
export const SIGNUP_STATUS_LABELS: Readonly<Record<SignupStatus, string>> = {
  su_notYet: "Not yet open",
  su_openLimited: "Open to priority audience",
  su_open: "Open to general public",
  su_closed: "Closed",
  su_unknown: "Unknown",
};

/** Admin dropdown labels, in `AVAILABILITY_STATUSES` order (`av_limited` is few SPACES). */
export const AVAILABILITY_STATUS_LABELS: Readonly<Record<AvailabilityStatus, string>> = {
  av_space: "Has availability",
  av_limited: "Limited availability",
  av_waitlist: "Waitlist only",
  av_fullNoWaitlist: "No availability",
  av_unknown: "Unknown",
};

const SIGNUP_SET: ReadonlySet<string> = new Set(SIGNUP_STATUSES);
const AVAILABILITY_SET: ReadonlySet<string> = new Set(AVAILABILITY_STATUSES);

export function isSignupStatus(value: unknown): value is SignupStatus {
  return typeof value === "string" && SIGNUP_SET.has(value);
}

export function isAvailabilityStatus(value: unknown): value is AvailabilityStatus {
  return typeof value === "string" && AVAILABILITY_SET.has(value);
}

export type StatusCoercionLogger = (message: string, detail: Record<string, unknown>) => void;

const consoleWarn: StatusCoercionLogger = (message, detail) => console.warn(message, detail);

function isAbsent(value: unknown): boolean {
  return value == null || (typeof value === "string" && value.trim() === "");
}

function coerce<T extends string>(
  value: unknown,
  field: "signupStatus" | "availabilityStatus",
  isMember: (v: unknown) => v is T,
  unknown: T,
  path: string,
  log: StatusCoercionLogger,
): T {
  if (isMember(value)) return value;
  const trimmed = typeof value === "string" ? value.trim() : value;
  if (isMember(trimmed)) return trimmed;
  if (!isAbsent(value)) {
    log(`[${path}] off-enum ${field} ${JSON.stringify(value)} coerced to ${unknown}`, { path, field, value, coercedTo: unknown });
  }
  return unknown;
}

/** A canonical `signupStatus`, or `su_unknown` (logged under `path` when the input was present but off-enum). */
export function coerceSignupStatus(value: unknown, path: string, log: StatusCoercionLogger = consoleWarn): SignupStatus {
  return coerce(value, "signupStatus", isSignupStatus, SIGNUP_STATUS_UNKNOWN, path, log);
}

/** A canonical `availabilityStatus`, or `av_unknown` (logged under `path` when the input was present but off-enum). */
export function coerceAvailabilityStatus(
  value: unknown,
  path: string,
  log: StatusCoercionLogger = consoleWarn,
): AvailabilityStatus {
  return coerce(value, "availabilityStatus", isAvailabilityStatus, AVAILABILITY_STATUS_UNKNOWN, path, log);
}
